import * as Tooltip from '@radix-ui/react-tooltip'
import type React from 'react'

/** Disables the button while `when` holds, and tells the user why. */
export type DisabledGuard = { when: boolean; reason: string }

/**
 * Wrap the app in one of these, above anything that renders a button. Radix keeps the window in
 * which a second tooltip opens without waiting on the provider, so a provider per button makes a
 * user scanning across several inactive buttons wait out the delay at every one of them.
 */
export const TooltipProvider = Tooltip.Provider

export const findActiveGuard = (
  guards: DisabledGuard | ReadonlyArray<DisabledGuard> | undefined
): DisabledGuard | undefined => {
  if (guards === undefined) return undefined
  return ('when' in guards ? [guards] : guards).find((guard) => guard.when)
}

// Cancelling the click also cancels what the element would do with it: submit a form, follow a
// link, or open the file picker behind a label.
export const swallowClick: React.MouseEventHandler = (event) => event.preventDefault()

type DisabledReasonProps = {
  /** The reason to show, or `undefined` while the button is active. */
  reason: string | undefined
  children: React.ReactNode
}

/**
 * Shows `reason` when the wrapped element is hovered or focused. The wrapper is always present so
 * that a guard starting or stopping to hold does not remount the element and drop focus; only the
 * content comes and goes.
 */
export const DisabledReason = ({ reason, children }: DisabledReasonProps) => (
  <Tooltip.Root delayDuration={300}>
    <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
    {reason !== undefined && (
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          sideOffset={6}
          className="pan-body-small z-[100] max-w-xs whitespace-normal rounded-md bg-black px-2 py-1 text-white shadow-sm"
        >
          {reason}
          <Tooltip.Arrow className="fill-black" />
        </Tooltip.Content>
      </Tooltip.Portal>
    )}
  </Tooltip.Root>
)
